import { DarkMode, LightMode, Logout, Menu as MenuIcon } from '@mui/icons-material';
import { AppBar, Box, IconButton, Toolbar, Typography, Avatar, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { APP_NAME, ROUTES } from '@/config/app.config';
import { useAuthStore } from '@/stores/use-auth-store';
import { useThemeStore } from '@/stores/use-theme-store';
import { gradients } from '@/theme/theme';

interface HeaderProps {
    onToggleSidebar: () => void;
}

export function Header({ onToggleSidebar }: HeaderProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, isAuthenticated, logout } = useAuthStore();
    const { mode, toggleTheme } = useThemeStore();

    const handleLogout = () => {
        logout();
        navigate(ROUTES.home);
    };

    return (
        <AppBar
            position="fixed"
            elevation={0}
            sx={{
                background: mode === 'dark' ? gradients.dark : gradients.primary,
                backdropFilter: 'blur(20px)',
                zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
        >
            <Toolbar sx={{ gap: 2 }}>
                {isAuthenticated && (
                    <IconButton color="inherit" onClick={onToggleSidebar} edge="start">
                        <MenuIcon />
                    </IconButton>
                )}

                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                        cursor: 'pointer',
                        fontFamily: '"JetBrains Mono", monospace',
                    }}
                    onClick={() => navigate(ROUTES.home)}
                >
                    {APP_NAME}
                </Typography>

                <Box sx={{ flexGrow: 1 }} />

                <Tooltip title={mode === 'dark' ? t('common.lightMode') : t('common.darkMode')}>
                    <IconButton color="inherit" onClick={toggleTheme}>
                        {mode === 'dark' ? <LightMode /> : <DarkMode />}
                    </IconButton>
                </Tooltip>

                {isAuthenticated && user ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                            sx={{
                                width: 32,
                                height: 32,
                                bgcolor: 'rgba(255,255,255,0.2)',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                            }}
                        >
                            {user.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                            {user.name}
                        </Typography>
                        <Tooltip title={t('common.signOut')}>
                            <IconButton color="inherit" onClick={handleLogout} size="small">
                                <Logout fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography
                            variant="body2"
                            sx={{ cursor: 'pointer', fontWeight: 500, '&:hover': { opacity: 0.8 } }}
                            onClick={() => navigate(ROUTES.login)}
                        >
                            {t('common.signIn')}
                        </Typography>
                    </Box>
                )}
            </Toolbar>
        </AppBar>
    );
}

export default Header;
